import type { MaterialId } from '../content/materials';
import type { PacketId } from './types';

/**
 * What `zip` packs one packet from each of its inputs into. It rides the pipe as a single packet,
 * but it is not a material: nothing sells it and no source yields it, so it stays a tuple until a
 * `map` recipe destructures it into a product.
 */
export type MaterialTuple = readonly MaterialId[];

/**
 * What a sale settles into. It is the proceeds of a material rather than a material itself, so no
 * recipe consumes it, no filter matches it and no source yields it — the only thing that accepts one
 * is a Subscriber running `deposit()`. This is the one point where a stream stops carrying matter and
 * starts carrying a number, which is `Observable<T>` visibly changing its `T`.
 */
export interface Cash {
  readonly cash: number;
}

/** Everything a packet can carry that is still stuff — as opposed to the proceeds of stuff. */
export type Matter = MaterialId | MaterialTuple;

export type PacketMaterial = Matter | Cash;

export interface Packet {
  id: PacketId;
  material: PacketMaterial;
  bornTick: number;
}

export function isTuple(material: PacketMaterial): material is MaterialTuple {
  return Array.isArray(material);
}

export function isCash(material: PacketMaterial): material is Cash {
  return typeof material === 'object' && !Array.isArray(material);
}

export function isMaterial(material: PacketMaterial): material is MaterialId {
  return typeof material === 'string';
}

export function isMatter(material: PacketMaterial): material is Matter {
  return !isCash(material);
}

/**
 * How a packet's contents read in an error the player sees. A tuple keeps its brackets, so
 * "cannot sell a [iron, carbon] packet" says plainly that nothing ever turned the pair into a
 * product.
 */
export function materialText(material: PacketMaterial): string {
  if (isTuple(material)) return `[${material.join(', ')}]`;
  if (isCash(material)) return cashText(material.cash);
  return material;
}

export function cashText(amount: number): string {
  return amount < 0 ? `-$${Math.abs(amount)}` : `$${amount}`;
}
